<template>
  <div>
    <validation-provider v-slot="{ errors }" name="Inventories" rules="checks">
      <inventories v-model="model.inventories" :is-read-only="isReadOnly" />
      <error
        v-if="errors[0]"
        message="Seleccione al menos un inventario"
        variant="no-border"
      />
    </validation-provider>

    <div v-if="hasRiasecInventories" class="mt-3">
      <section-title
        content="Las tres áreas principales identificadas por el evaluado son las siguientes:"
        has-line
      />

      <div v-for="item in areasByEvaluatedMainTasks" :key="item.area.id">
        <answer
          :is-read-only="isReadOnly"
          :value="item"
          @input="onAnswer(item, $event)"
        />
      </div>
    </div>

    <div v-if="hasCirinoArciInventory">
      <section-title content="Inventario Cirino y ARCI:" has-line />
      <div class="mx-1">
        <p>
          Son herramientas para identificar intereses vocacionales en
          estudiantes y adultos desde tercer grado en adelante. Sus escalas
          permiten evaluar intereses comunes en cada nivel escolar, fomentando
          el desarrollo de identidad y autoconcepto, así como la aceptación de
          gustos individuales. Además, ayudan a descubrir intereses vinculados a
          áreas STEM como ciencias y matemáticas.
        </p>

        <p>
          La tipología que emplean estos inventarios clasifica los intereses en
          áreas que reflejan motivaciones específicas derivadas de necesidades
          generales como logro, autonomía, afiliación, seguridad, empoderamiento
          y estimulación. Los intereses pueden ser manuales, sociales,
          comerciales o científicos, y se agrupan en categorías como:
        </p>
      </div>
      <div class="pt-2">
        <div v-for="item in areasByCirinoArciInventory" :key="item.area.id">
          <answer
            :is-read-only="isReadOnly"
            :value="item"
            @input="onAnswer(item, $event)"
          />
        </div>
      </div>
    </div>

    <div v-if="hasReadingFree" class="mt-3">
      <section-title
        content="Resultados de acuerdo con los grupos de carreras o “clusters” definidas por el Departamento del Trabajo de EUA y categorías de interés.:"
        has-line
      />

      <section-title
        container-class="tag-badge-main-info mt-4 mb-4"
        content="Grupo de Carrera o “cluster”"
        icon="fas fa-user-tie"
      />

      <clusters
        v-model="model.alternativeGoals"
        aggregable
        is-alternative
        :is-read-only="isReadOnly"
        :max="maxAlternativeGoals"
        @add="addAlternativeGoal"
        @remove="removeAlternativeGoal($event)"
      />

      <div class="my-3">
        <section-title
          container-class="tag-badge-main-info mt-4 mb-4"
          content="Categorías de interés: Personas/ Datos/ Cosas"
          icon="fas fa-user-tie"
        />
        <clusters
          v-model="categoriesOfInteres"
          :is-alternative="false"
          :is-read-only="isReadOnly"
        />
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from '@/mixins/alert.mixin.js';

import SectionTitle from '@/shared/components/SectionTitle.vue';
import Error from 'custom-error';

import Inventories from './components/Inventories.vue';
import Answer from './components/AreaAnswer.vue';
import Clusters from './components/Clusters.vue';

import Categories from '@/utils/constants/vocationalEvaluationInstrumentAreaCategory.js';

import { extend } from 'vee-validate';
extend('checks', {
  validate: (value) => value?.filter((x) => x.checked)?.length > 0,
  message: 'Seleccione al menos un inventario',
});

//constants
const GROUP_CAREER_ID = 6;
const MAX_ALTERNATIVE_GOALS = 8;
const CIRINO = 1;
const ARCI = 2;
const READING_FREE = 5;
const NON_RIASEC_INVENTORIES = [CIRINO, ARCI, READING_FREE];

export default {
  name: 'InterestProfile',
  components: {
    SectionTitle,
    Inventories,
    Answer,
    Clusters,
    Error,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      maxAlternativeGoals: MAX_ALTERNATIVE_GOALS,
    };
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    areasByEvaluatedMainTasks() {
      return this.areasByCategory(Categories.EvaluatedMainTasks);
    },
    areasByCirinoArciInventory() {
      return this.areasByCategory(Categories.CirinoArciInventory);
    },
    categoriesOfInteres() {
      return this.model?.goals?.filter((x) => x.goal.id != GROUP_CAREER_ID);
    },
    checkedInventoryIds() {
      return (this.model.inventories ?? [])
        .filter((x) => x.checked)
        .map((x) => x.id);
    },
    hasCirinoArciInventory() {
      return this.checkedInventoryIds.some((id) => [CIRINO, ARCI].includes(id));
    },
    hasReadingFree() {
      return this.checkedInventoryIds.includes(READING_FREE);
    },
    hasNonRiasecInventory() {
      return this.checkedInventoryIds.some((id) =>
        NON_RIASEC_INVENTORIES.includes(id)
      );
    },
    // Shows the RIASEC section whenever at least one checked inventory is NOT
    // Cirino/ARCI/Reading Free (i.e. a RIASEC-type inventory is marked as
    // administered). Previously this only checked the ABSENCE of Cirino/ARCI/
    // Reading Free, so marking a RIASEC inventory (e.g. ONET) together with
    // Cirino/ARCI hid the section even though RIASEC scoring was still needed.
    hasRiasecInventories() {
      if (!this.checkedInventoryIds.length) {
        return !this.isReadOnly;
      }

      return this.checkedInventoryIds.some(
        (id) => !NON_RIASEC_INVENTORIES.includes(id)
      );
    },
  },
  mounted() {
    if (!this.value.alternativeGoals?.length) {
      this.addAlternativeGoal();
    }
  },
  methods: {
    areasByCategory(category) {
      return (this.model.areas ?? []).filter(
        (x) => x.area?.category === category
      );
    },
    onAnswer(item, value) {
      const index = this.model.areas.findIndex(
        (a) => a.area?.id === item.area.id
      );
      if (index !== -1) {
        this.model.areas[index] = value;
      }
    },
    addAlternativeGoal() {
      if ((this.model.alternativeGoals?.length ?? 0) >= MAX_ALTERNATIVE_GOALS) {
        return;
      }

      const goal =
        this.model.alternativeGoals?.[0]?.goal ??
        (this.model.goals ?? []).find((x) => x?.goal?.id == GROUP_CAREER_ID)
          ?.goal;
      if (!goal) return;

      this.model.alternativeGoals.push({
        goal,
        number: null,
        profession: null,
        isAlternative: true,
      });
    },
    removeAlternativeGoal(key) {
      this.model.alternativeGoals.splice(key, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .whodas-title .dot {
  background-color: white !important;
}
::v-deep .whodas-title .dot .title-icon {
  color: #ff7058 !important;
}
</style>
